import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Pagination,
  Grid,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  CameraAlt as CameraAltIcon,
  Schedule as ScheduleIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import dayjs from "dayjs";
import {
  Invoice,
  InvoiceFilters as IInvoiceFilters,
  CATEGORY_LABELS,
  CATEGORY_COLORS,
  TIPO_DISPLAY_NAMES,
  InvoiceTipo,
} from "../types/invoice";
import { ApiService } from "../services/api";
import { PhotoViewerModal } from "./PhotoViewerModal";
import EditInvoiceModal from "./EditInvoiceModal";
import InvoiceDetailViewer from "./InvoiceDetailViewer";

const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"];
const MAX_UPLOAD_SIZE = 10 * 1024 * 1024; // 10MB

interface InvoiceListProps {
  invoices: Invoice[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  filters?: IInvoiceFilters;
  sortOrder?: "ASC" | "DESC";
  onPageChange: (page: number) => void;
  onInvoiceDeleted?: () => void;
}

const InvoiceList: React.FC<InvoiceListProps> = ({
  invoices,
  pagination,
  filters = {},
  sortOrder = "DESC",
  onPageChange,
  onInvoiceDeleted,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoInvoiceId, setPhotoInvoiceId] = useState<number | null>(null);
  const [tipoMenuAnchor, setTipoMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [updatingTipo, setUpdatingTipo] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [dragOverInvoiceId, setDragOverInvoiceId] = useState<number | null>(null);
  const [uploadingInvoiceIds, setUploadingInvoiceIds] = useState<Set<number>>(new Set());
  const [editModalInvoice, setEditModalInvoice] = useState<Invoice | null>(null);
  const [detailViewerInvoiceId, setDetailViewerInvoiceId] = useState<number | null>(null);
  const fileInputRefs = React.useRef<Record<number, HTMLInputElement | null>>({});

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    invoice: Invoice
  ) => {
    console.log("📝 Menu opened for invoice:", invoice);
    console.log("📝 Invoice ID:", invoice.id, "Type:", typeof invoice.id);
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedInvoice here - keep it for the delete dialog
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedInvoice) {
      console.log("❌ No invoice selected for deletion");
      return;
    }

    console.log("🗑️ Starting delete process for invoice:", selectedInvoice);
    console.log(
      "🗑️ Invoice ID:",
      selectedInvoice.id,
      "Type:",
      typeof selectedInvoice.id
    );

    setDeleting(true);
    try {
      console.log(
        "🗑️ Calling ApiService.deleteInvoice with ID:",
        selectedInvoice.id
      );
      await ApiService.deleteInvoice(selectedInvoice.id);
      console.log("✅ Delete successful");
      setSnackbar({
        open: true,
        message: "Fatura eliminada com sucesso",
        severity: "success",
      });
      setDeleteDialogOpen(false);
      setSelectedInvoice(null);
      onInvoiceDeleted?.();
    } catch (error) {
      console.error("❌ Delete failed:", error);
      setSnackbar({
        open: true,
        message: "Erro ao eliminar fatura",
        severity: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedInvoice(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handlePhotoClick = (invoiceId: number) => {
    setPhotoInvoiceId(invoiceId);
    setPhotoModalOpen(true);
  };

  const handlePhotoModalClose = () => {
    setPhotoModalOpen(false);
    setPhotoInvoiceId(null);
  };

  const handlePhotoDeleted = () => {
    setSnackbar({
      open: true,
      message: "Foto deletada com sucesso",
      severity: "success",
    });
    onInvoiceDeleted?.();
  };

  const handleTipoClick = (
    event: React.MouseEvent<HTMLElement>,
    invoice: Invoice
  ) => {
    event.stopPropagation();
    setTipoMenuAnchor(event.currentTarget);
    setEditingInvoice(invoice);
  };

  const handleTipoMenuClose = () => {
    setTipoMenuAnchor(null);
    setEditingInvoice(null);
  };

  const handleTipoChange = async (newTipo: InvoiceTipo) => {
    if (!editingInvoice) return;

    setUpdatingTipo(true);
    try {
      await ApiService.updateInvoice(editingInvoice.id, { tipo: newTipo });
      setSnackbar({
        open: true,
        message: "Tipo atualizado com sucesso",
        severity: "success",
      });
      onInvoiceDeleted?.(); // Trigger refresh
    } catch (error) {
      console.error("Error updating tipo:", error);
      setSnackbar({
        open: true,
        message: "Erro ao atualizar tipo",
        severity: "error",
      });
    } finally {
      setUpdatingTipo(false);
      handleTipoMenuClose();
    }
  };

  const handlePostponeClick = () => {
    handleMenuClose();
    if (selectedInvoice) {
      handlePostpone(selectedInvoice.id);
    }
  };

  const handlePostpone = async (invoiceId: number) => {
    try {
      await ApiService.postponeInvoice(invoiceId);
      setSnackbar({
        open: true,
        message: "Despesa adiada para o próximo mês",
        severity: "success",
      });
      onInvoiceDeleted?.(); // Trigger refresh
    } catch (error) {
      console.error("Error postponing invoice:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Erro ao adiar despesa";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  const handlePhotoUpload = async (invoiceId: number, file: File) => {
    if (uploadingInvoiceIds.has(invoiceId)) return;
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      setSnackbar({ open: true, message: "Formato não suportado (use JPEG, PNG, GIF, WebP ou PDF)", severity: "error" });
      return;
    }
    if (file.size > MAX_UPLOAD_SIZE) {
      setSnackbar({ open: true, message: "Ficheiro demasiado grande (máximo 10MB)", severity: "error" });
      return;
    }
    setUploadingInvoiceIds((prev) => new Set(prev).add(invoiceId));
    try {
      await ApiService.uploadInvoicePhoto(invoiceId, file);
      setSnackbar({ open: true, message: "Foto adicionada com sucesso", severity: "success" });
      onInvoiceDeleted?.(); // Trigger refresh
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erro ao carregar foto";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setUploadingInvoiceIds((prev) => {
        const next = new Set(prev);
        next.delete(invoiceId);
        return next;
      });
    }
  };

  const handleDrop = (e: React.DragEvent, invoiceId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverInvoiceId(null);
    const file = e.dataTransfer.files[0];
    if (file) handlePhotoUpload(invoiceId, file);
  };

  const handleDragOver = (e: React.DragEvent, invoiceId: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer.types.includes("Files")) return;
    setDragOverInvoiceId(invoiceId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragOverInvoiceId(null);
  };

  const getCategoryIcon = (categoria: string) => {
    switch (categoria) {
      case "regular":
        return <ReceiptIcon fontSize="small" />;
      case "a_faturar":
        return <BusinessIcon fontSize="small" />;
      case "despesa":
        return <TrendingDownIcon fontSize="small" />;
      case "despesa_a_faturar":
        return <AccountBalanceIcon fontSize="small" />;
      default:
        return <ReceiptIcon fontSize="small" />;
    }
  };

  // Data de emissão afastada mais de 2 meses da data de registo (provável erro)
  const hasDateMismatch = (invoice: Invoice) => {
    if (!invoice.data_emissao || !invoice.created_at) return false;
    const diff = dayjs(invoice.created_at).diff(dayjs(invoice.data_emissao), "month", true);
    return Math.abs(diff) > 2;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("pt-PT");
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return "-";
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  if (invoices.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <ReceiptIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Nenhuma fatura encontrada
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ajuste os filtros ou escaneie novas faturas no app móvel
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={1.5}>
        {invoices.map((invoice) => (
          <Card
            key={invoice.id}
            sx={
              hasDateMismatch(invoice)
                ? { bgcolor: "#FFF9C4", border: "1px solid", borderColor: "#FBC02D" }
                : undefined
            }
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              {/* Header: Categoria, Tipo, Valor e Ações */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Box
                    sx={{
                      color: CATEGORY_COLORS[invoice.categoria],
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {getCategoryIcon(invoice.categoria)}
                  </Box>
                  <Chip
                    label={CATEGORY_LABELS[invoice.categoria]}
                    size="small"
                    sx={{
                      bgcolor: `${CATEGORY_COLORS[invoice.categoria]}20`,
                      color: CATEGORY_COLORS[invoice.categoria],
                      fontWeight: "bold",
                    }}
                  />
                  {invoice.tipo && (
                    <Chip
                      label={TIPO_DISPLAY_NAMES[invoice.tipo]}
                      size="small"
                      variant="outlined"
                      onClick={(e) => handleTipoClick(e, invoice)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    />
                  )}
                  {invoice.mes_relatorio &&
                    invoice.data_emissao &&
                    invoice.mes_relatorio !==
                      new Date(invoice.data_emissao).getMonth() + 1 && (
                      <Chip
                        label="Adiada"
                        size="small"
                        icon={<ScheduleIcon />}
                        sx={{
                          bgcolor: "warning.light",
                          color: "warning.dark",
                          fontWeight: "bold",
                        }}
                      />
                    )}
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {formatCurrency(invoice.valor)}
                  </Typography>
                  {invoice.photo_url ? (
                    <IconButton
                      size="small"
                      onClick={() => handlePhotoClick(invoice.id)}
                      sx={{ color: "primary.main" }}
                      title="Ver foto da fatura"
                    >
                      <CameraAltIcon />
                    </IconButton>
                  ) : (
                    <>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                        hidden
                        ref={(el) => { fileInputRefs.current[invoice.id] = el; }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handlePhotoUpload(invoice.id, file);
                          e.target.value = "";
                        }}
                      />
                      <Box
                        onDrop={(e) => handleDrop(e, invoice.id)}
                        onDragOver={(e) => handleDragOver(e, invoice.id)}
                        onDragLeave={handleDragLeave}
                        onClick={() => {
                          if (!uploadingInvoiceIds.has(invoice.id)) {
                            fileInputRefs.current[invoice.id]?.click();
                          }
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          border: "1.5px dashed",
                          borderColor: dragOverInvoiceId === invoice.id ? "primary.main" : "divider",
                          bgcolor: dragOverInvoiceId === invoice.id ? "primary.50" : "transparent",
                          cursor: uploadingInvoiceIds.has(invoice.id) ? "default" : "pointer",
                          transition: "all 0.15s ease",
                          pointerEvents: uploadingInvoiceIds.has(invoice.id) ? "none" : "auto",
                          "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: "action.hover",
                          },
                          minWidth: 0,
                          flexShrink: 0,
                        }}
                        title="Arraste uma imagem ou clique para selecionar"
                      >
                        {uploadingInvoiceIds.has(invoice.id) ? (
                          <CircularProgress size={16} />
                        ) : (
                          <>
                            <CloudUploadIcon sx={{ fontSize: 16, color: dragOverInvoiceId === invoice.id ? "primary.main" : "text.disabled" }} />
                            <Typography variant="caption" color={dragOverInvoiceId === invoice.id ? "primary.main" : "text.disabled"} noWrap>
                              Foto
                            </Typography>
                          </>
                        )}
                      </Box>
                    </>
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, invoice)}
                    sx={{ color: "text.secondary" }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Conteúdo em 2 colunas */}
              <Grid container spacing={2}>
                {/* Coluna 1 */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        ID:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        #{invoice.id}
                      </Typography>
                    </Box>

                    {invoice.numero_fatura && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Número:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {invoice.numero_fatura}
                        </Typography>
                      </Box>
                    )}

                    {invoice.nif_emitente && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Emitente:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {invoice.emitente_nome_curto ||
                            invoice.emitente_nome ||
                            invoice.nif_emitente}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>

                {/* Coluna 2 */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack spacing={1}>
                    {invoice.nif_adquirente && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Adquirente:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {invoice.adquirente_nome_curto ||
                            invoice.adquirente_nome ||
                            invoice.nif_adquirente}
                        </Typography>
                      </Box>
                    )}

                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Data Emissão:
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        title={
                          hasDateMismatch(invoice)
                            ? "Data de emissão afastada mais de 2 meses da data de registo"
                            : undefined
                        }
                        sx={
                          hasDateMismatch(invoice)
                            ? { bgcolor: "#FFEB3B", px: 0.5, borderRadius: 0.5 }
                            : undefined
                        }
                      >
                        {formatDate(invoice.data_emissao)}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Registado:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatDate(invoice.created_at)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>

              {/* Descrição (full width abaixo das 2 colunas) */}
              {invoice.descricao && (
                <Box mt={2}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    display="block"
                  >
                    Descrição:
                  </Typography>
                  <Typography variant="caption" sx={{ fontStyle: "italic" }}>
                    {invoice.descricao}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            mt: 3,
            p: 2,
            backgroundColor: "background.paper",
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Pagination
            count={pagination.pages}
            page={pagination.page}
            onChange={(_, page) => onPageChange(page)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
          />
          <Typography variant="body2" color="text.secondary">
            Mostrando {invoices.length} de {pagination.total} faturas
            {` - Página ${pagination.page} de ${pagination.pages}`}
          </Typography>
        </Box>
      )}

      {/* Results summary - only show if no pagination */}
      {pagination.pages <= 1 && (
        <Box mt={2} textAlign="center">
          <Typography variant="caption" color="text.secondary">
            Mostrando {invoices.length} de {pagination.total} faturas
          </Typography>
        </Box>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (selectedInvoice) setDetailViewerInvoiceId(selectedInvoice.id);
          }}
        >
          <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
          Ver detalhes
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (selectedInvoice) setEditModalInvoice(selectedInvoice);
          }}
        >
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Editar
        </MenuItem>
        {selectedInvoice &&
          ["despesa", "despesa_a_faturar"].includes(
            selectedInvoice.categoria
          ) && (
            <MenuItem onClick={handlePostponeClick}>
              <ScheduleIcon sx={{ mr: 1 }} fontSize="small" />
              Adiar para próximo mês
            </MenuItem>
          )}
        <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Eliminar
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar Eliminação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem a certeza de que deseja eliminar esta fatura?
            {selectedInvoice && (
              <>
                <br />
                <br />
                <strong>Fatura:</strong>{" "}
                {selectedInvoice.numero_fatura || "N/A"}
                <br />
                <strong>Valor:</strong> {formatCurrency(selectedInvoice.valor)}
                <br />
                <strong>Data:</strong>{" "}
                {formatDate(selectedInvoice.data_emissao)}
              </>
            )}
            <br />
            <br />
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "A eliminar..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tipo Selection Menu */}
      <Menu
        anchorEl={tipoMenuAnchor}
        open={Boolean(tipoMenuAnchor)}
        onClose={handleTipoMenuClose}
      >
        <MenuItem disabled sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
          Selecione o tipo:
        </MenuItem>
        <Divider />
        {Object.entries(TIPO_DISPLAY_NAMES).map(([tipo, label]) => (
          <MenuItem
            key={tipo}
            onClick={() => handleTipoChange(tipo as InvoiceTipo)}
            disabled={updatingTipo}
            selected={editingInvoice?.tipo === tipo}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Photo Viewer Modal */}
      {photoInvoiceId && (
        <PhotoViewerModal
          open={photoModalOpen}
          invoiceId={photoInvoiceId}
          onClose={handlePhotoModalClose}
          onPhotoDeleted={handlePhotoDeleted}
        />
      )}

      {/* Detail Viewer */}
      <InvoiceDetailViewer
        open={detailViewerInvoiceId !== null}
        startInvoiceId={detailViewerInvoiceId}
        filters={filters}
        sortOrder={sortOrder}
        onClose={() => setDetailViewerInvoiceId(null)}
      />

      {/* Edit Invoice Modal */}
      <EditInvoiceModal
        open={!!editModalInvoice}
        invoice={editModalInvoice}
        onClose={() => setEditModalInvoice(null)}
        onUpdated={() => {
          setSnackbar({ open: true, message: "Fatura atualizada com sucesso", severity: "success" });
          onInvoiceDeleted?.();
        }}
      />
    </Box>
  );
};

export default InvoiceList;
